import React, { useEffect, useRef, useState } from 'react'
import ReactDOM from 'react-dom'
import { Prompt, Button, Checkbox, CloseButton, DropdownMenu, Icon, IconButton, Input, Media, MediaTile, Message, Slider, Spinner, Toggle, TooltipLabel } from 'components'
import { useAppDispatch, useAppSelector, useDownloadMedia, useLocalStorage } from 'app/hooks'
import { createLLMContent, createLLMContext, removeLLMContext, retrieveLLMContext } from 'actions/adminActions'
import { DynamicContentType, EditorFieldType, LLMGenerateSettings, LLMGenerateType, LLMRefineSettings, MediaType, PageSEOMode, PromptType } from 'app/types'
import { DynamicContent } from './DynamicContent'
import * as fnc from 'helpers/fnc'
import { logger } from 'helpers/logger'
import { GenericEditor } from 'views/AdminPage/Views/GenericEditor'
import { pageDef } from './InlineContentEditor'
import { pushNotification, showPrompt } from 'actions/appActions'
import {
    FacebookShareButton,
    FacebookIcon,
    TwitterShareButton,
    TwitterIcon,
} from 'react-share'
import { getPlaceholderImage } from 'helpers/config'
import { deleteLLMContext } from 'services/adminServices'
import { Tooltip } from 'recharts'

const RESULT_PAGE_COUNT = 21

const defaultContext = {
    id: null,
    label: '',
    context: '',
}

enum RetrieveType {
    SearchOnline = 'search-online',
    Library = 'library',
}

interface LLMContentHelperOptions {
    placeholder: string,
    defaultValue: string,
}

interface LLMContentHelperProps {
    app: AppData,
    organization: OrganizationData,
    id: string,
    title: string,
    value: string,
    options: LLMContentHelperOptions,
    onChange: (value: string) => void,
    onSubmit: (value: string) => void,
    onClose: () => void,
    target: HTMLElement,
    scrollElem: HTMLElement,
    top: number,
    left: number,
    type: LLMGenerateType,
    embedded: boolean,
    prompt: string,
    downloadBeforeSubmit?: boolean,
    currentEditData?: any,
}

const refineItems = Object.keys(LLMRefineSettings).map((x) => {
    return {
        text: x.charAt(0).toUpperCase() + x.slice(1),
        value: x.toLowerCase()
    }
})

const generateItems = Object.keys(LLMGenerateSettings).map((x) => {
    let wordCount = 0
    switch (x.toLowerCase()) {
        case 'long':
            wordCount = 100
            break
        case 'medium':
            wordCount = 50
            break
        case 'short':
            wordCount = 25
            break
    }
    return {
        text: x.charAt(0).toUpperCase() + x.slice(1) + ` (${wordCount}w)`,
        value: x.toLowerCase(),
    }
})

const testData = {
    title: 'This is a title',
    subtitle: 'This is a subtitle',
    body: 'This is the body of the content',
    elements: [
        {
            title: 'Element 1',
            body: 'This is the body of element 1',
        },
        {
            title: 'Element 2',
            body: 'This is the body of element 2',
        },
    ]
}

export function LLMContentHelper(props: LLMContentHelperProps) {
    const { app, organization, id, title, target, type, options, embedded, downloadBeforeSubmit, currentEditData } = { options: {}, ...props }
    const [position, setPosition] = useState<{ left: number; top: number }>(null)
    const [targetInitial, setTargetInitial] = useState<{ left: number; top: number }>(null)
    const [settings, setSettings] = useState({
        directive: null,
        structure: [],
        automateStructure: true,
        structureLength: 0,
        placeholders: false,
    })
    const [value, setValue] = useState(props.value)
    const [prompt, setPrompt] = useState('')
    const [temperature, setTemperature] = useState(0.5)
    const [generating, setGenerating] = useState(false)
    const [dragging, setDragging] = useState(false)
    const [result, setResult] = useState(options?.defaultResult)
    const [resultSelected, setResultSelected] = useState(null)
    const [resultPage, setResultPage] = useState(0)
    const [submitting, setSubmitting] = useState(false)
    const [submitMessage, setSubmitMessage] = useState('')
    const [error, setError] = useState(null)
    const [debug, setDebug] = useState(null)
    const [debugPage, setDebugPage] = useState(0)
    const [pendingChanges, setPendingChanges] = useState(false)
    const [showResult, setShowResult] = useState(true)
    const [isReviewingContent, setIsReviewingContent] = useState(false)

    const [currentContext, setCurrentContext] = useLocalStorage('llm-context', null)
    const [editContext, setEditContext] = useState(false)
    const llmContext = useAppSelector(state => state.admin.llmContext)
    const context = currentContext ? llmContext?.find((x) => x.id === currentContext) : null
    const [contextSaving, setContextSaving] = useState(false)
    const [contextPrompt, setContextPrompt] = useState(null)
    const [mediaHelper, setMediaHelper] = useState(null)
    const [closePrompt, setClosePrompt] = useState(null)
    const [debugPrompt, setDebugPrompt] = useState(null)

    const allMedia = useAppSelector((state) => state.app.media)
    const dragStartRef = useRef<{ x: number; y: number; left: number; top: number }>()
    const dragOffset = useRef<{ x: number; y: number }>()
    const elemRef = useRef<HTMLDivElement>(null)
    const settingsKey = `llm-settings-${id}-${type}`
    const dispatch = useAppDispatch()
    const downloadMedia = useDownloadMedia(app, organization)

    const style: React.CSSProperties = {
        position: 'absolute',
        left: position?.left,
        top: position?.top,
        cursor: dragging ? 'grabbing' : 'grab',
    }
    useEffect(() => {
        if (llmContext != null) {
            return
        }

        dispatch(retrieveLLMContext({ app, organization }))
    }, [llmContext])

    useEffect(() => {
        console.log("[DEBUG] value changed to:", JSON.stringify(value))
    }, [value])

    useEffect(() => {
        let cachedSettings = localStorage.getItem(settingsKey)
        cachedSettings = cachedSettings ? JSON.parse(cachedSettings) : {}
        switch (type) {
            case LLMGenerateType.SingleField:
                setSettings({
                    directive: null,
                    structure: [],
                    ...cachedSettings
                })
                break
            case LLMGenerateType.SingleContent:
                setSettings({
                    directive: null,
                    structure: [DynamicContentType.Blurb],
                    placeholders: false,
                    ...cachedSettings
                })
                break
            case LLMGenerateType.PageContent:
                setSettings({
                    directive: null,
                    automateStructure: true,
                    structureLength: 0,
                    structure: [
                        DynamicContentType.Splash,
                        DynamicContentType.Blurb,
                        DynamicContentType.Accordian
                    ],
                    placeholders: false,
                    ...cachedSettings
                })
                break
            case LLMGenerateType.RetrieveImage:
                setSettings({
                    search: '',
                    directive: RetrieveType.SearchOnline
                })
                break
            case LLMGenerateType.PageSEO:
                setSettings({
                    directive: null,
                    includePageContent: true,
                    mode: PageSEOMode.Generate,
                    ...cachedSettings
                })
                break
        }
    }, [type])

    useEffect(() => {
        if (!target) {
            setPosition({ left: props.left, top: props.top })
            return
        }

        if (targetInitial) return
        const rect = target.getBoundingClientRect()
        setTargetInitial({ left: rect.left, top: rect.top })
    }, [target, targetInitial])

    useEffect(() => {
        if (!target || !targetInitial || position) return

        function updatePosition() {
            const rect = target.getBoundingClientRect()
            // Calculate offset based on target initial, apply difference to props.left and props.top
            const offsetTop = rect.top - targetInitial.top
            const offsetLeft = rect.left - targetInitial.left
            setPosition({ left: props.left + offsetLeft, top: props.top + offsetTop })
        }

        updatePosition() // Initial positioning

        // // Scroll and resize listeners
        // window.addEventListener('scroll', updatePosition, true)
        // window.addEventListener('resize', updatePosition)
        // if (props.scrollElem) {
        //     props.scrollElem.addEventListener('scroll', updatePosition)
        // }

        // // MutationObserver to track DOM changes
        // const observer = new MutationObserver(updatePosition)
        // observer.observe(document.body, { attributes: true, childList: true, subtree: true })

        // return () => {
        //     window.removeEventListener('scroll', updatePosition, true)
        //     window.removeEventListener('resize', updatePosition)
        //     observer.disconnect() // Clean up observer
        //     if (props.scrollElem) {
        //         props.scrollElem.removeEventListener('scroll', updatePosition)
        //     }
        // }
    }, [target, targetInitial])

    function getContextEdited() {
        return (context && editContext && context.id == editContext.id && JSON.stringify(context) != JSON.stringify(editContext)) || (editContext && !editContext.id)
    }

    function handleDebug() {
        setDebugPrompt(true)
    }

    function handleContextSafeAction(action, data) {
        if (getContextEdited()) {
            setContextPrompt({ type: PromptType.Confirm, title: 'Discard Changes?', message: 'You have unsaved changes. Do you want to discard them?', action, data })
        } else {
            handleContextAction(action, data)
        }
    }

    function handleAddContext() {
        const newContext = { ...defaultContext }
        handleContextSafeAction('add', newContext)
    }

    function handleChangeContext(x) {
        handleContextSafeAction('change', x)
    }

    function handleEditContextField(field, value) {
        // Check for change
        const newEditContext = { ...editContext }
        newEditContext[field] = value
        setEditContext(newEditContext)
    }

    function handleEditContext(x) {
        handleContextSafeAction('edit', x)
    }

    function handleSaveContext() {
        setContextSaving(true)
        dispatch(createLLMContext({ app, organization, data: editContext }))
            .then((x) => {
                setContextSaving(false)
                setCurrentContext(x.payload.data.id)
                setEditContext(null)
            })
    }

    function handleCancelContextEdit() {
        handleContextSafeAction('cancel')
    }

    function handleDeleteContext() {
        setContextPrompt({ type: PromptType.Confirm, title: 'Delete Context?', message: `Are you sure you want to delete the context "${editContext?.label}"? This action cannot be undone.`, action: 'delete' })
    }

    function handleCloneContext() {
        handleContextSafeAction('clone')
    }

    function handleContextAction(action, data = null) {
        switch (action) {
            case 'cancel':
                setEditContext(null)
                break
            case 'delete':
                setContextSaving(true)
                dispatch(removeLLMContext({ app, organization, data: editContext }))
                    .then((x) => {
                        setContextSaving(false)
                        setEditContext(null)
                        setCurrentContext(null)
                    })
                break
            case 'change':
                setCurrentContext(data.id)
                break
            case 'edit':
                if (data) {
                    const match = llmContext.find((x => x.id == data))
                    setEditContext({ ...match })
                    setCurrentContext(data)
                } else {
                    if (context) {
                        setEditContext({ ...context })
                    } else {
                        setEditContext({ ...defaultContext })
                    }
                }
                break
            case 'add':
                setCurrentContext(null)
                setEditContext(data)
                break
            case 'clone':
                const clonedContext = { ...context, id: null, label: `${context.label} (copy)` }
                setEditContext(clonedContext)
                break
        }
    }

    function handleContextPromptResponse(response: boolean) {
        if (response) {
            handleContextAction(contextPrompt.action)
        }
        setContextPrompt(null)
    }

    function handleClosePromptResponse(response: boolean) {
        if (response) {
            if (props.onClose) {
                props.onClose()
            }
        }
        setClosePrompt(null)
    }

    function handleMouseDown(event: React.MouseEvent) {
        if (!elemRef.current) return
        setDragging(true)
        dragOffset.current = {
            x: event.clientX - position.left,
            y: event.clientY - position.top,
        }
        document.addEventListener('mousemove', handleMouseMove)
        document.addEventListener('mouseup', handleMouseUp)
    }

    function handleMouseMove(event: MouseEvent) {
        if (!dragOffset.current) return
        setPosition({
            left: event.clientX - dragOffset.current.x,
            top: event.clientY - dragOffset.current.y,
        })
    }

    function handleMouseUp() {
        dragOffset.current = null
        setDragging(false)
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
    }

    function cacheSettings(newSettings) {
        console.log("[DEBUG] cacheSettings - field being set, newSettings:", newSettings)
        localStorage.setItem(settingsKey, JSON.stringify(newSettings))
        setSettings(newSettings)
    }

    function handleSetting(field, value) {
        console.log("[DEBUG] handleSetting - field:", field, "value:", value, "current settings:", settings)
        const newSettings = { ...settings }
        newSettings[field] = value
        console.log("[DEBUG] handleSetting - newSettings after update:", newSettings)
        cacheSettings(newSettings)
    }

    function handlePlaceholders(placeholders) {
        handleSetting('placeholders', placeholders)
    }

    function handleDirective(directive: string) {
        handleSetting('directive', directive)
    }

    function handleStructure(item: string, ix = null) {
        const newSettings = { ...settings }
        newSettings.structure = [...settings.structure]
        if (ix !== null) {
            newSettings.structure[ix] = item
        }
        cacheSettings(newSettings)
    }

    function handleRemoveStructure(ix) {
        const newSettings = { ...settings }
        newSettings.structure = [...settings.structure]
        newSettings.structure.splice(ix, 1)
        cacheSettings(newSettings)
    }

    function handleAddStructure() {
        const newSettings = { ...settings }
        newSettings.structure = [...settings.structure]
        newSettings.structure.push(DynamicContentType.Blurb)
        cacheSettings(newSettings)
    }

    function handlePrompt(x) {
        setPrompt(x)
    }

    function handleLibrary() {
        let media = []
        if (app) {
            media = Object.values(allMedia).filter(x => x.appId == app.meta.id)
        } else if (organization) {
            media = Object.values(allMedia).filter(x => x.organizationId == organization.id)
        }
        media = media.filter((x) => x.mediaTypeId == MediaType.Image)
        setResult(media)
    }

    async function handleGenerate() {
        setGenerating(true)
        setError(true)
        const data = {
            prompt: prompt,
            options: { id, temperature, instruction: context?.context },
            type,
        }
        if (value) {
            data.options.initial = value
        }
        if (settings) {
            data.options = { ...data.options, ...settings }
        }

        // For PageSEO, pass pageId if includePageContent is enabled
        if (type == LLMGenerateType.PageSEO && settings.includePageContent && options?.pageId) {
            data.options.pageId = options.pageId
        }

        // For PageSEO Review mode, pass current values and ensure context is included
        if (type == LLMGenerateType.PageSEO && data.options.mode === PageSEOMode.Review) {
            data.options.currentValues = {
                seoDescription: currentEditData?.seoDescription || '',
                seoKeywords: currentEditData?.seoKeywords || '',
                breadcrumbTitle: currentEditData?.breadcrumbTitle || '',
            }
            data.options.reviewMode = true
            // Ensure instruction/context is preserved for review mode
            if (!data.options.instruction && context?.context) {
                data.options.instruction = context.context
            }
        }

        if (type == LLMGenerateType.SocialMediaPost) {
            // Create filtered data that only uses title, subtitle, body, and elements keys
            data.options.input = fnc.filterObject(options.data, ['title', 'subtitle', 'body', 'elements'])
        }
        setDebug(null)

        const ret = await dispatch(createLLMContent({ data, app, organization }))
        setGenerating(false)
        if (ret.meta.requestStatus != 'fulfilled') {
            setError(`Error - ${ret.payload?.error?.message}`)
            if (ret.payload?.error?.log) {
                setDebug(ret.payload.error.log)
            }
        } else {
            let newResult = 'content' in ret.payload ? ret.payload.content : ret.payload
            setDebug(ret.payload?.log)
            const imageResults = {}

            if (type == LLMGenerateType.PageContent || type == LLMGenerateType.SingleContent) {
                let proms = []
                let replaceMedia = []
                if (settings.placeholders) {
                    const placeholderMedia = Object.values(allMedia).find(x => {
                        if (x.link != 'placeholder.jpg') return false
                        if (app && x.appId != app.meta.id) return false
                        if (organization && x.organizationId == organization.id) return false
                        return true
                    })
                    replaceMedia = (x) => {
                        if (placeholderMedia) {
                            x.media = [{ mediaId: placeholderMedia.id, mediaTypeId: MediaType.Image }]
                        } else {
                            // If not use hardcoded placeholder
                            let placeholderUrl = getPlaceholderImage()
                            x.media = [{ url: placeholderUrl, mediaTypeId: MediaType.Image }]
                        }
                    }
                } else {
                    replaceMedia = (x) => {
                        return dispatch(createLLMContent({ data: { prompt: x.media, type: LLMGenerateType.RetrieveImage }, app, organization }))
                            .then((y) => {
                                const urls = y.payload
                                const randomImage = urls[Math.floor(Math.random() * urls.length)]
                                x.media = [{ url: randomImage, mediaTypeId: MediaType.Image, prompt: x.media, result: urls }]
                            })
                    }
                }
                const fixContent = (x) => {
                    if (x.media && typeof x.media == 'string') {
                        proms.push(replaceMedia(x))
                    }
                    if (x.elements) {
                        x.elements.forEach(fixContent)
                    }
                }
                newResult.forEach(fixContent)
                await Promise.all(proms)
            }

            // Strip quotes and whitespace
            if (typeof newResult === 'string') {
                newResult = newResult.replace(/["']/g, '')
            } else if (Array.isArray(newResult) && type == LLMGenerateType.SingleContent) {
                newResult = newResult[0]
            }

            setResult(newResult)
            setShowResult(true)
            // Mark that we're now reviewing generated content
            setIsReviewingContent(true)
            console.log("[DEBUG] handleGenerate - Results received, isReviewingContent set to true")
        }
    }

    function handleClose(force = false) {
        if (mediaHelper) {
            handleMediaHelperClose()
            return
        }
        // Check if there are unsaved changes
        if (props.onClose) {
            if (!force) {
                setClosePrompt({
                    type: PromptType.Confirm,
                    title: 'Close AI helper?',
                    message: 'Any unsaved changes will be discarded.',
                })
            } else {
                props.onClose()
            }
            /*dispatch(showPrompt({ type: PromptType.Confirm, title: 'Close AI helper?', message: 'Any unsaved changes will be discarded.' }))
                .then(x => {
                    if (x.payload) {
                        props.onClose()
                    }
                })*/
        }
    }

    function handleChange(x) {
        setValue(x)
        if (props.onChange) {
            props.onChange(x)
        }
    }

    async function handleSubmit(x) {
        if (props.onSubmit) {

            setSubmitting(true)
            if(type == LLMGenerateType.RetrieveImage && downloadBeforeSubmit && typeof x == 'string' && x.startsWith('https://')) {
                // We need to add this media to our library first
                const media = await downloadMedia(x)
                x = media.mediaId
            }

            const ret = await props.onSubmit(x ? x : value)
            setSubmitting(false)
            if (ret != null) {
                // Display message, then close
                setSubmitMessage(ret)
                await new Promise(resolve => setTimeout(resolve, 1000)) // 1 second delay
            }
            handleClose(true)
        }
    }

    function handleReject() {
        setResult(null)
        setPendingChanges(false)
        setIsReviewingContent(false)
        setShowResult(true)
    }

    function handleRefine() {
        handleChange(result)
        setResult(null)
    }

    function handleAccept() {
        console.log("[DEBUG] ACCEPT - type:", type, "isReviewingContent:", isReviewingContent, "result:", result)
        // For PageSEO review operation, directly submit and close
        if (type == LLMGenerateType.PageSEO && isReviewingContent) {
            console.log("[DEBUG] ACCEPT - PageSEO review - submitting directly")
            const newValue = {
                ...currentEditData,
                seoDescription: result?.seoDescription || currentEditData?.seoDescription,
                seoKeywords: result?.seoKeywords || currentEditData?.seoKeywords,
                breadcrumbTitle: result?.breadcrumbTitle || currentEditData?.breadcrumbTitle,
            }
            console.log("[DEBUG] ACCEPT - Submitting value:", JSON.stringify(newValue))
            // Submit directly without intermediate step
            if (props.onSubmit) {
                setSubmitting(true)
                props.onSubmit(newValue).then(() => {
                    console.log("[DEBUG] ACCEPT - PageSEO submit successful, closing helper")
                    setSubmitting(false)
                    setPendingChanges(false)
                    setIsReviewingContent(false)
                    handleClose(true)
                }).catch((err) => {
                    setSubmitting(false)
                    logger.error('Error submitting PageSEO changes:', err)
                })
            }
        } else if (type == LLMGenerateType.SingleContent || type == LLMGenerateType.PageContent) {
            console.log("[DEBUG] ACCEPT - BRANCH FIRING - SingleContent or PageContent")
            handleSubmit(result)
        } else if (type == LLMGenerateType.RetrieveImage) {
            console.log("[DEBUG] ACCEPT - BRANCH FIRING - RetrieveImage")
            if (Array.isArray(result) && resultSelected != null) {
                handleSubmit(result[resultSelected])
            }
        } else {
            console.log("[DEBUG] ACCEPT - BRANCH FIRING - else (handleSubmit)")
            handleSubmit(result)
        }
    }


    function handleMedia(x, opt, e) {
        const newMediaHelper = {
            id: 'media-helper',
            type: LLMGenerateType.RetrieveImage,
            target: e.target,
            title: 'Search for image',
            top: e.pageY,
            left: e.pageX,
            options: { defaultValue: x.prompt, defaultResult: x.result },
            media: x
        }
        setMediaHelper(newMediaHelper)
    }

    function handleMediaHelperSubmit(x) {
        const newResult = JSON.parse(JSON.stringify(result).replace(mediaHelper.media.url, x))
        setResult(newResult)
        setShowResult(true)
    }

    function handleMediaHelperClose() {
        setMediaHelper(null)
    }

    function handleCopy(value, key) {
        if (fnc.copyToClipboard(value)) {
            dispatch(pushNotification({ message: key + ' copied to clipboard' }))
        } else {
            dispatch(pushNotification({ message: 'There was a problem copying to the clipboard.' }))
        }
    }

    function renderPagination(currentPage, totalPages, onPageChange) {
        if (totalPages <= 1) return null;

        // Create an array of page numbers to display
        const pageNumbers = [];
        const maxVisiblePages = 5;

        // Calculate visible page range
        let startPage = Math.max(0, currentPage - Math.floor(maxVisiblePages / 2));
        let endPage = Math.min(totalPages - 1, startPage + maxVisiblePages - 1);

        // Adjust if we're near the start or end
        if (endPage - startPage + 1 < maxVisiblePages) {
            startPage = Math.max(0, endPage - maxVisiblePages + 1);
        }

        // Generate page buttons
        for (let i = startPage; i <= endPage; i++) {
            pageNumbers.push(
                <Button
                    key={i}
                    tertiary={currentPage !== i}
                    onClick={() => onPageChange(i)}
                    className={currentPage === i ? 'active' : ''}
                >
                    {i + 1}
                </Button>
            );
        }

        return (
            <div className="pagination-controls">
                <Button
                    onClick={() => onPageChange(0)}
                    disabled={currentPage === 0}
                    tertiary
                >
                    &lt;&lt;
                </Button>
                <Button
                    onClick={() => onPageChange(currentPage - 1)}
                    disabled={currentPage === 0}
                    tertiary
                >
                    &lt;
                </Button>
                {pageNumbers}
                <Button
                    onClick={() => onPageChange(currentPage + 1)}
                    disabled={currentPage === totalPages - 1}
                    tertiary
                >
                    &gt;
                </Button>
                <Button
                    onClick={() => onPageChange(totalPages - 1)}
                    disabled={currentPage === totalPages - 1}
                    tertiary
                >
                    &gt;&gt;
                </Button>
                <span className="page-info">Page {currentPage + 1} of {totalPages}</span>
            </div>
        );
    }

    function renderDebugInfo(debugData) {
        // If debugData is an array, handle pagination
        if (Array.isArray(debugData)) {
            const itemsPerPage = 1
            const totalPages = Math.ceil(debugData.length / itemsPerPage)
            const startIndex = debugPage * itemsPerPage
            const currentItem = debugData[startIndex]

            return (
                <div className="debug-content">
                    {renderSingleDebugItem(currentItem)}
                </div>
            )
        }

        // If not an array, render a single debug item
        return renderSingleDebugItem(debugData)
    }

    function renderSingleDebugItem(debugData) {
        if (!debugData) return <p>No debug data available</p>;

        const {
            prompt: inputPrompt,
            initOptions,
            fullResponse,
            additionalOptions,
            attempts,
            ...other
        } = debugData;

        let parsedResponse = '';
        try {
            parsedResponse = JSON.stringify(JSON.parse(fullResponse), null, 2);
        } catch (error) {
            console.error("Error parsing fullResponse:", error);
            parsedResponse = 'Error parsing response. It may not be valid JSON.';
        }

        return (
            <div className="debug-item">
                {inputPrompt && <React.Fragment>
                    <TooltipLabel htmlFor="debug-prompt" tooltip="This is the final prompt that was sent to the AI for generation.">Prompt</TooltipLabel>
                    <p>{inputPrompt}</p>
                </React.Fragment>}
                {parsedResponse && <React.Fragment>
                    <TooltipLabel htmlFor="debug-response" tooltip="This is the raw response from the AI.">Response</TooltipLabel>
                    <pre className="debug-json">{parsedResponse}</pre>
                </React.Fragment>}
                {initOptions?.system && <React.Fragment>
                    <TooltipLabel htmlFor="debug-context" tooltip="This is the system context that was used for the AI generation.">System Context</TooltipLabel>
                    <p>{initOptions.system}</p>
                </React.Fragment>}
                {additionalOptions && <React.Fragment>
                    <TooltipLabel htmlFor="debug-options" tooltip="These are the additional options that were used for the AI generation.">Additional Options</TooltipLabel>
                    <pre className="debug-json">{JSON.stringify(additionalOptions, null, 2)}</pre>
                </React.Fragment>}
                {attempts > 0 && <React.Fragment>
                    <TooltipLabel htmlFor="debug-attempts" tooltip="This is the number of requests required to generate the content.">Requests</TooltipLabel>
                    <p>{attempts}</p>
                </React.Fragment>}
                {other && Object.keys(other).length > 0 && <React.Fragment>
                    <TooltipLabel htmlFor="debug-other" tooltip="This is any other debug information that was returned.">Other Debug Info</TooltipLabel>
                    <pre className="debug-json">{JSON.stringify(other, null, 2)}</pre>
                </React.Fragment>}
            </div>
        );
    }

    function getResult() {
        const submitElem = <div className="row submit">
            <Button onClick={handleReject}> Reject</Button>
            {type == LLMGenerateType.SingleField && <Button onClick={handleRefine}> Refine</Button>}
            <Button tertiary onClick={handleAccept}>Accept</Button>
            {debug && <Button onClick={handleDebug}>Debug</Button>}
        </div>
        switch (type) {
            case LLMGenerateType.SingleField:
                return <div className="column result">
                    <h5>Generated Content</h5>
                    <div className="row">
                        <Input textarea value={result} onChange={setResult} />
                    </div>
                    {submitElem}
                </div>
            case LLMGenerateType.SingleContent:
                return <div className="column result">
                    <h5>Generated Content</h5>
                    <div className="row result-content scrollable">
                        <DynamicContent dynamicContentTypeId={settings.structure[0]} onMedia={handleMedia} {...result} app={app} organization={organization} />
                    </div>
                    {submitElem}
                </div>
            case LLMGenerateType.PageContent:
                return <div className="column result">
                    <h5>Generated Content</h5>
                    <div className="row result-content scrollable">
                        <div className="column">
                            {result.map((x, ix) => {
                                return <DynamicContent dynamicContentTypeId={x.dynamicContentTypeId || x.type} onMedia={handleMedia} {...x} app={app} organization={organization} />
                            })}
                        </div>
                    </div>
                    {submitElem}
                </div>
            case LLMGenerateType.PageSEO:
                // Check if there are any suggestions (for Review mode)
                const hasRevisions = settings.mode === PageSEOMode.Review && (
                    (result.seoDescription && result.seoDescription.trim().length > 0) ||
                    (result.seoKeywords && result.seoKeywords.trim().length > 0) ||
                    (result.breadcrumbTitle && result.breadcrumbTitle.trim().length > 0)
                )
                
                // If Review mode with no revisions, show "No Suggestions" message
                if (settings.mode === PageSEOMode.Review && !hasRevisions) {
                    return <div className="column result llm-seo-review">
                        {result.review && <div className="review-highlight">
                            <div className="column review-section">
                                <h5>Review</h5>
                                <div className="review-paragraph">
                                    <p>{result.review}</p>
                                </div>
                            </div>
                        </div>}
                        <div className="review-highlight">
                            <div className="column generated-values">
                                <h5>Suggested Improvements</h5>
                                <div className="no-suggestions-message">
                                    <p>No Suggestions</p>
                                </div>
                            </div>
                        </div>
                        <div className="column actions">
                            <div className="row submit">
                                <Button onClick={handleReject}>Okay</Button>
                                {debug && <Button onClick={handleDebug}>Debug</Button>}
                            </div>
                        </div>
                    </div>
                }
                
                return <div className="column result llm-seo-review">
                    {settings.mode === PageSEOMode.Review && result.review && <div className="review-highlight">
                        <div className="column review-section">
                            <h5>Review</h5>
                            <div className="review-paragraph">
                                <p>{result.review}</p>
                            </div>
                        </div>
                    </div>}
                    <div className="review-highlight">
                        <div className="column generated-values">
                            <h5>{settings.mode === PageSEOMode.Review && result.fields ? 'Suggested Improvements' : 'Generated Values'}</h5>
                            <div className="field-item">
                            <label>Meta Description</label>
                            <div className="generated-value">
                                <Input 
                                    textarea 
                                    value={result.seoDescription || ''} 
                                    onChange={(val) => setResult({ ...result, seoDescription: val })}
                                    maxLength="160"
                                    rows="2"
                                />
                                <small>{(result.seoDescription || '').length}/160 chars</small>
                            </div>
                        </div>
                        <div className="field-item">
                            <label>Meta Keywords</label>
                            <div className="generated-value">
                                <Input 
                                    textarea 
                                    value={result.seoKeywords || ''} 
                                    onChange={(val) => setResult({ ...result, seoKeywords: val })}
                                    placeholder="Comma-separated keywords"
                                    rows="2"
                                />
                            </div>
                        </div>
                        <div className="field-item">
                            <label>Breadcrumb Title</label>
                            <div className="generated-value">
                                <Input 
                                    value={result.breadcrumbTitle || ''} 
                                    onChange={(val) => setResult({ ...result, breadcrumbTitle: val })}
                                    placeholder="Navigation title"
                                />
                            </div>
                        </div>
                        </div>
                    </div>
                    <div className="column actions">
                        {submitElem}
                    </div>
                </div>
            case LLMGenerateType.RetrieveImage:
                // Strip query string
                let finalResult = result
                let paginate = false
                if (finalResult.length > RESULT_PAGE_COUNT) {
                    const startIndex = Math.min(result.length, RESULT_PAGE_COUNT * resultPage);
                    const endIndex = Math.min(result.length, startIndex + RESULT_PAGE_COUNT);
                    finalResult = finalResult.slice(startIndex, endIndex);
                    paginate = true
                }
                if (settings.search && settings.search.length > 0) {
                    finalResult = finalResult.filter((x) => {
                        if (x.name && x.name.toLowerCase().includes(settings.search.toLowerCase())) {
                            return true; // Keep this item if it matches the search
                        }
                        if (x.url && x.url.toLowerCase().includes(settings.search.toLowerCase())) {
                            return true; // Keep this item if the URL matches the search
                        }
                        return false
                    })
                }
                return <div className="column result">
                    <h5>Pick Image</h5>
                    <div className="row">
                        <Input value={settings.search} clear onChange={(x) => handleSetting('search', x)} placeholder="Search for images" type="text" />
                    </div>
                    <div className="row result-content scrollable" key={resultPage}>
                        {finalResult.map((x, ix) => {
                            if (typeof x == 'string') {
                                const finalUrl = x//x.replace(/\?.*$/, '')
                                const media = { mediaTypeId: MediaType.Image, url: finalUrl, name: 'Image ' + (ix + 1) }
                                return <MediaTile selected={ix == resultSelected} media={media} onClick={() => setResultSelected(ix)} hoverShow={true} />
                            } else {
                                // Handle case where result is an object with a url property
                                return <MediaTile selected={ix == resultSelected} app={app} organization={!app ? organization : null} media={x} onClick={() => setResultSelected(ix)} hoverShow={true} />
                            }
                            // return <Media url={finalUrl} onClick={() => setResultSelected(ix)}/>
                        })}
                        {paginate && <div className="row result-content-pagination">
                            <Button onClick={() => setResultPage(resultPage - 1)} disabled={resultPage <= 0}>&lt;</Button>
                            <Button onClick={() => setResultPage(resultPage + 1)} disabled={resultPage >= Math.ceil(result.length / RESULT_PAGE_COUNT) - 1}>&gt;</Button>
                        </div>}
                    </div>
                    {resultSelected != null && submitElem}
                </div>
            case LLMGenerateType.SocialMediaPost:
                return <div className="column result">
                    <h5>Result</h5>
                    <div className="row result-content scrollable">
                        <div className="column">
                            {result.map((x, ix) => {
                                return <div className="row">
                                    <div className="column">
                                        <DynamicContent dynamicContentTypeId={DynamicContentType.Blurb} {...x} />
                                        <div className="row copy-options">
                                            <Button tertiary onClick={() => handleCopy(x.title, 'Title')}>Copy Title</Button>
                                            <Button tertiary onClick={() => handleCopy(x.body, 'Body')}>Copy Body</Button>
                                        </div>
                                    </div>
                                </div>

                            })}
                        </div>
                    </div>
                    {/* {submitElem} */}
                </div>
        }
    }

    function getContextEditor() {
        if (contextPrompt) {
            return <Prompt nested prompt={contextPrompt} onDismiss={handleContextPromptResponse} />
        }

        const isBlank = Object.keys(editContext).length === 0
        return <div className={`row context${contextSaving ? ' disabled' : ''}`} key={editContext?.id}>
            <div className="column">
                <div className="row title">
                    <h5>{context ? 'Edit Context' : 'Set New Context'}</h5>
                </div>
                <TooltipLabel docLink="llm-context" htmlFor="select-context" tooltip="Select the context that you wish to edit.">Context</TooltipLabel>
                {llmContext.length > 0 && <div id='select-context' className="row context-select">
                    {editContext?.id && <DropdownMenu items={llmContext.map((x, ix) => ({ text: x.label, value: x.id }))} value={currentContext} onChange={(x) => handleEditContext(x)} />}
                    {editContext?.id && <IconButton icon="fas fa-plus" onClick={handleAddContext} />}
                    {context && <IconButton icon="fas fa-copy" onClick={handleCloneContext} />}
                    {context && <IconButton icon="fas fa-trash" onClick={handleDeleteContext} />}
                </div>}
                <TooltipLabel htmlFor="context-label" docLink="llm-content-label" tooltip="Label for the context. This is used to identify the context in the list of contexts.">Label</TooltipLabel>
                <Input id="context-label" placeholder="Context label" value={editContext?.label} onChange={(x) => handleEditContextField('label', x)} />
                <TooltipLabel docLink="llm-context-value" htmlFor="context-value" tooltip="This is the context that will be used to generate the content.">Context</TooltipLabel>
                <Input id="context-value" textarea placeholder="Here you can define the context for which the AI should work within" defaultValue={editContext?.context} onChange={(x) => handleEditContextField('context', x)} />
                <div className="row context-options">
                    <Button secondary icon="fas fa-save" disabled={isBlank} onClick={handleSaveContext}>Save</Button>
                    <Button icon="fas fa-times" onClick={handleCancelContextEdit}>Cancel</Button>
                </div>
                {contextSaving && <Spinner />}
            </div>
        </div>
    }

    function getGeneratorInput() {
        let inputElem = null

        let generateText = type == LLMGenerateType.RetrieveImage ? ['Getting Images...', 'Get Image'] : ['Generating...', 'Generate']
        // For PageSEO, customize the button text based on mode
        if (type == LLMGenerateType.PageSEO) {
            generateText = settings.mode === PageSEOMode.Review ? ['Reviewing...', 'Review'] : ['Generating...', 'Generate']
        }
        const submitElem = <div className="row submit">
            {generating && <Button tertiary disabled>{generateText[0]}</Button>}
            {!generating && <Button tertiary onClick={handleGenerate}>{generateText[1]}</Button>}
        </div>

        const titleElem = <div className="row title">
            <h5 className="noselect">{title ? title : 'AI Assist'}</h5>
        </div>

        let temperatureElem = null
        // let temperatureElem = <React.Fragment>
        //     <TooltipLabel htmlFor="temperature" docLink="llm-temperature" tooltip="Temperature controls the randomness of the output. A lower value (0.0) will make the output more predictable, while a higher value (1.0) will make it more creative.">Temperature</TooltipLabel>
        //     <div id="temperature" className="temperature">
        //         <div className="top-left">
        //             <span>Predictable</span>
        //         </div>
        //         <div className="top-right">
        //             <span>Creative</span>
        //         </div>
        //         <Slider simple min={0.0} max={1} step={0.1} value={temperature} onChange={setTemperature} />
        //     </div>
        // </React.Fragment>
        let optionsElem = <div className="options">
            <TooltipLabel htmlFor="placeholders" docLink="llm-placeholders" tooltip="Use placeholder images instead of generated images.">Placeholders</TooltipLabel>
            <Checkbox id="placeholders" value={settings.placeholders} onChange={handlePlaceholders}>Use Placeholder Images</Checkbox>
        </div>
        const availableActions = value ? refineItems : generateItems
        let actionsElem = <div className="suggestions">
            <TooltipLabel htmlFor="suggestions" docLink="llm-suggestion" tooltip="Adjust the length of the content generated">Section Length</TooltipLabel>
            <Toggle id="suggestions-actions" items={availableActions} value={settings.directive} onChange={handleDirective} toggleOff />

        </div>
        const acceptedSet = new Set([DynamicContentType.Blurb, DynamicContentType.Accordian, DynamicContentType.Splash, DynamicContentType.Numbered, DynamicContentType.Grid, DynamicContentType.Items, DynamicContentType.Steps, DynamicContentType.Tabbed])
        const typeItems = Object.keys(DynamicContentType).filter((x) => isNaN(x) && acceptedSet.has(DynamicContentType[x])).map((x) => {
            return { value: DynamicContentType[x], text: x?.toReadable() }
        })
        const contextElem = llmContext ? <div className="context-select">
            <TooltipLabel htmlFor="context-select" docLink="llm-context" llm="llm-context" tooltip="Select the context for the AI to work within. This is the context that will be used to generate the content.">Context</TooltipLabel>
            {llmContext.length > 0 && <div className="row">
                <DropdownMenu id="context-select" items={llmContext?.map((x) => ({ text: x.label, value: x.id }))} onChange={setCurrentContext} value={currentContext} selectText="Set Context" />
                <IconButton icon={context ? "fas fa-pen" : "fas fa-plus"} onClick={() => handleEditContext()} />
            </div>}
            {llmContext.length == 0 && <Button onClick={() => handleEditContext()}>Set Context</Button>}
        </div> : <div className="row"><Button disabled>Loading Contexts</Button></div>

        switch (type) {
            default:
            case LLMGenerateType.SingleField:
                return <div className="column generate">
                    {titleElem}
                    {contextElem}
                    {props.value && <div className="initial">
                        <TooltipLabel htmlFor="initial-value" docLink="llm-initial-value" tooltip="This is the initial value used to generate the content. This is the text sent to the AI to be modified">Initial Value</TooltipLabel>
                        <Input id="initial-value" textarea defaultValue={value} onChange={setValue} />
                    </div>}
                    <TooltipLabel htmlFor="content-prompt" docLink="llm-content-prompt" tooltip="This is the prompt used to generate the content.">Prompt</TooltipLabel>
                    <Input id="content-prompt" textarea value={prompt} defaultValue={options.defaultValue} placeholder={options.placeholder || "Enter prompt here"} onChange={handlePrompt} />
                    {actionsElem}
                    {temperatureElem}
                    {submitElem}
                </div>
            case LLMGenerateType.SingleContent: {
                return <React.Fragment>
                    <div className="column generate">
                        {titleElem}
                        {contextElem}
                        {value && <DynamicContent {...value} />}
                        <TooltipLabel htmlFor="content-type" docLink="llm-content-type" tooltip="Select the type of content you want to generate.">Content Type</TooltipLabel>
                        <DropdownMenu id="content-structure" items={typeItems} onChange={(x) => handleStructure(x, 0)} value={settings.structure[0]} />
                        <TooltipLabel htmlFor="content-prompt" docLink="llm-content-prompt" tooltip="This is the prompt used to generate the content.">Prompt</TooltipLabel>
                        <Input id="content-prompt" textarea value={prompt} defaultValue={options.defaultValue} placeholder={options.placeholder || "Enter prompt here"} onChange={handlePrompt} />
                        {actionsElem}
                        {optionsElem}
                        {temperatureElem}
                        {submitElem}
                    </div>
                    {!result && <div className="column content-example result">
                        <h5>Preview</h5>
                        <div className="result-content scrollable">
                            <DynamicContent dynamicContentTypeId={settings.structure[0]} {...testData} />
                        </div>
                    </div>}
                </React.Fragment>
            }
            case LLMGenerateType.PageContent: {
                return <React.Fragment>
                    <div className="column generate">
                        {titleElem}
                        {contextElem}
                        {/* {value && <DynamicContent {...value} />} */}
                        <TooltipLabel htmlFor="content-type" docLink="llm-content-type" tooltip="Select the structure of the content you want to generate.">Content Structure</TooltipLabel>
                        <Checkbox value={settings.automateStructure} onChange={(x) => handleSetting('automateStructure', x)} id="automate-sections">Automatically pick content structure</Checkbox>
                        {!settings.automateStructure && <React.Fragment>
                            <div className="page-sections">
                                {settings.structure?.map((x, ix) => {
                                    return <div className="row page-section">
                                        <span>{`Section ${ix + 1}`}</span>
                                        <DropdownMenu items={typeItems} onChange={(y) => handleStructure(y, ix)} value={x} />
                                        <IconButton icon="fas fa-times" onClick={() => handleRemoveStructure(ix)} />
                                    </div>
                                })}
                            </div>
                            <Button icon="fas fa-plus" onClick={handleAddStructure}>Add new section</Button>
                        </React.Fragment>}
                        {settings.automateStructure && type == LLMGenerateType.PageContent && <React.Fragment>
                            <TooltipLabel htmlFor="section" tooltip="How many sections to include in the page" docLink="section-count">Section Count</TooltipLabel>
                            <Toggle id="section-count"
                                items={[
                                    { text: 'Auto', value: 0 },
                                    ...[...Array(10).keys()].map((x) => ({ text: `${x + 1}`, value: x + 1 }))
                                ]}
                                value={settings.structureLength}
                                onChange={(x) => handleSetting('structureLength', x)}
                            />
                        </React.Fragment>}
                        <TooltipLabel htmlFor="content-prompt" docLink="llm-content-prompt" tooltip="This is the prompt used to generate the content.">Prompt</TooltipLabel>
                        <Input id="content-prompt" textarea value={prompt} defaultValue={options.defaultValue} placeholder={options.placeholder || "Enter prompt here"} onChange={handlePrompt} />
                        {actionsElem}
                        {optionsElem}
                        {temperatureElem}
                        {submitElem}
                    </div>
                    {!result && <div className="column content-example result">
                        <h5>Example</h5>
                        {!settings.automateStructure && <div className="result-content scrollable">
                            {settings.structure?.map((x) => {

                                return <DynamicContent dynamicContentTypeId={x} {...testData} />
                            })}
                        </div>}
                        {settings.automateStructure && <p>
                            Result will be displayed here
                        </p>}
                    </div>}
                </React.Fragment>
                return submitElem
            }
            case LLMGenerateType.RetrieveImage: {
                return <React.Fragment>
                    <div className="column generate">
                        {titleElem}
                        <TooltipLabel htmlFor="image-prompt" docLink="llm-image-prompt" tooltip="This is the search term used to find images. You can use any term you like, but the more specific the better.">Search Term</TooltipLabel>
                        <Input id="image-prompt" value={prompt} defaultValue={options.defaultValue} placeholder={options.placeholder || "Enter prompt here"} onChange={handlePrompt} onSubmit={handleGenerate} />
                        {submitElem}
                        <h5>or</h5>
                        <Button onClick={handleLibrary}>Pick from Library</Button>
                    </div>
                </React.Fragment>
            }
            case LLMGenerateType.SocialMediaPost: {
                return <div className="column generate">
                    {titleElem}
                    <TooltipLabel htmlFor="social-prompt" docLink="llm-social-prompt" tooltip="This is the prompt used to generate the social media post.">Prompt</TooltipLabel>
                    <Input id="social-prompt" value={prompt} textarea defaultValue={options.defaultValue} placeholder={options.placeholder || "Enter prompt here"} onChange={handlePrompt} onSubmit={handleGenerate} />
                    {/* {optionsElem} */}
                    {actionsElem}
                    {contextElem}
                    {submitElem}
                </div>
            }
            case LLMGenerateType.PageSEO: {
                console.log("[DEBUG] getGeneratorInput PageSEO - value.seoDescription:", value?.seoDescription, "currentEditData.seoDescription:", currentEditData?.seoDescription)
                return <div className="column generate">
                    {titleElem}
                    {contextElem}
                    <div className="seo-mode-selector">
                        <TooltipLabel htmlFor="page-seo-mode" docLink="llm-page-seo-mode" tooltip="Choose between generating new SEO values or reviewing existing ones.">Mode</TooltipLabel>
                        <Toggle 
                            id="page-seo-mode"
                            value={settings.mode}
                            onChange={(mode) => handleSetting('mode', mode)}
                            items={[
                                { value: PageSEOMode.Generate, text: 'Generate' },
                                { value: PageSEOMode.Review, text: 'Review' }
                            ]}
                        />
                    </div>
                    {settings.mode === PageSEOMode.Generate && <React.Fragment>
                        <TooltipLabel htmlFor="page-seo-prompt" docLink="llm-page-seo-prompt" tooltip="Enter a description of the page or topic to generate SEO fields for.">Page Description</TooltipLabel>
                        <Input id="page-seo-prompt" value={prompt} textarea defaultValue={options.defaultValue} placeholder={options.placeholder || "Enter page topic or description..."} onChange={handlePrompt} />
                    </React.Fragment>}
                    {settings.mode === PageSEOMode.Review && <React.Fragment>
                        <TooltipLabel htmlFor="page-seo-review-prompt" docLink="llm-page-seo-review-prompt" tooltip="Optional: Add specific guidance for the AI review of your current SEO fields.">Review Guidance (Optional)</TooltipLabel>
                        <Input id="page-seo-review-prompt" value={prompt} textarea defaultValue={options.defaultValue} placeholder={options.placeholder || "Add any specific SEO concerns or areas to focus on..."} onChange={handlePrompt} />
                    </React.Fragment>}
                    {options?.pageId && <React.Fragment>
                        <TooltipLabel htmlFor="include-page-content" docLink="llm-include-page-content" tooltip="Include the actual page content to generate more accurate SEO fields based on what's currently on the page.">Include Page Content</TooltipLabel>
                        <Checkbox id="include-page-content" value={settings.includePageContent} onChange={(x) => handleSetting('includePageContent', x)}>
                            Include current page content
                        </Checkbox>
                    </React.Fragment>}
                    {submitElem}
                </div>
            }
        }
    }

    if (generating) {
        style.filter = 'brightness(0.9)'
    }
    // if (!position) {
    // return null
    // }
    let bodyElem = null
    if (closePrompt) {
        bodyElem = <Prompt nested prompt={closePrompt} onDismiss={handleClosePromptResponse} />
    } else if (debugPrompt) {
        const showPagination = Array.isArray(debug) && debug.length > 1
        bodyElem = <div className="column debug">
            {showPagination && <h4>{`Debug Information ${debugPage + 1} of ${Math.ceil(debug.length / 1)}`}</h4>}
            {!showPagination && <h4>Debug Information</h4>}
            {renderDebugInfo(debug)}
            <div className="row debug-options">
                {/* Pagination controls for debug items */}
                {showPagination && (
                    <div className="debug-pagination row">
                        <Button
                            onClick={() => setDebugPage(debugPage - 1)}
                            disabled={debugPage <= 0}
                            tertiary
                        >
                            &lt;
                        </Button>
                        <Button
                            onClick={() => setDebugPage(debugPage + 1)}
                            disabled={debugPage >= Math.ceil(debug.length / 1) - 1}
                            tertiary
                        >
                            &gt;
                        </Button>
                    </div>
                )}
                <Button tertiary onClick={() => handleCopy(JSON.stringify(debug, null, 2), 'Debug JSON')}>Copy Debug JSON</Button>
                <Button onClick={() => setDebugPrompt(null)}>Close</Button>
            </div>
        </div>
    } else {
        bodyElem = <React.Fragment>
            {!embedded && <CloseButton onClick={handleClose} />}
            {/* {!embedded && <CloseButton onClick={handleClose} />}
            {embedded && (
                <div className="close-wrapper" style={{ display: 'flex', justifyContent: 'flex-end', position: 'relative', zIndex: 20 }}>
                    <CloseButton onClick={handleClose} />
                </div>
            )} */}
            <div className="row helper-dialog">
                {editContext && getContextEditor()}
                {!editContext && !(result && embedded) && getGeneratorInput()}
                {!editContext && result && showResult && getResult()}
                {mediaHelper && <LLMContentHelper embedded={true} {...mediaHelper} app={app} organization={organization} onSubmit={handleMediaHelperSubmit} onClose={handleMediaHelperClose} />}
            </div>
            {error && error.length > 0 && <React.Fragment>
                <div className="row error"><span>{error}</span></div>
                {debug && <div className="row"><Button onClick={handleDebug}>View Log</Button></div>}
            </React.Fragment>}
            {(generating || submitting) && <Spinner invert show showAfter={null} quotes={false} />}
            {submitMessage && <div className="success"><Icon noBg icon="fas fa-check" /><h4>{submitMessage}</h4></div>}
        </React.Fragment>
    }

    if (embedded) {
        return <React.Fragment>
            <CloseButton onClick={handleClose} />
            <div className={`column result embedded ${generating ? ' generating' : ''}`}>
                {bodyElem}
            </div>
        </React.Fragment>
    }

    const content = (
        <div ref={elemRef} className={`LLM-helper animate__animated animate__fadeInRight animate__faster${generating ? ' generating' : ''}${submitting ? ' submitting' : ''}`} style={style} onMouseDown={handleMouseDown} >
            {bodyElem}
        </div>
    )
    return ReactDOM.createPortal(content, document.body)
}
